import { AdditiveBlending, BufferAttribute, BufferGeometry, CanvasTexture, Points, PointsMaterial, type Vector3 } from 'three';

function glowTexture(): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.25, 'rgba(255,255,255,0.55)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);
  }
  return new CanvasTexture(canvas);
}

const texture = glowTexture();

/** Soft round glows (street lamps, tail lights) drawn as one batch of additive points. */
export class GlowPoints {
  readonly object: Points;
  private readonly positions: Float32Array;

  constructor(colour: string, size: number, readonly capacity: number) {
    this.positions = new Float32Array(capacity * 3);
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    geometry.setDrawRange(0, 0);
    const material = new PointsMaterial({ color: colour, size, map: texture, transparent: true, depthWrite: false, blending: AdditiveBlending, sizeAttenuation: true, fog: false });
    this.object = new Points(geometry, material);
    this.object.frustumCulled = false;
    this.object.visible = false;
  }

  /** `strength` 0..1 fades the whole batch in; at 0 it's hidden. */
  setStrength(strength: number): void {
    this.object.visible = strength > 0.02;
    (this.object.material as PointsMaterial).opacity = strength;
  }

  set(points: readonly Vector3[]): void {
    const count = Math.min(points.length, this.capacity);
    for (let i = 0; i < count; i += 1) {
      const p = points[i]!;
      this.positions[i * 3] = p.x;
      this.positions[i * 3 + 1] = p.y;
      this.positions[i * 3 + 2] = p.z;
    }
    this.object.geometry.setDrawRange(0, count);
    (this.object.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
  }
}
